import { useDataSubmit, useDataValue, useDataChange } from 'Simple/Data.js'
import { isUnscheduledAppointment } from 'Data/format.js'

/** @type {import('Simple/types.js').useDataOnChange} */
export default function useDataOnChange(props, data) {
  let submit = useDataSubmit({
    viewPath: props.viewPath,
    context: 'appointment_overlay',
  })
  let submitTab = useDataSubmit({
    viewPath: props.viewPath,
    context: 'tab',
  })
  let appointment_id = useDataValue({
    context: 'appointment_overlay',
    viewPath: props.viewPath,
    path: 'appointment_id',
  })
  let is_external_any = useDataValue({
    viewPath: props.viewPath,
    context: 'appointment_overlay',
    path: 'is_external_any',
  })
  let is_emergency = useDataValue({
    viewPath: props.viewPath,
    context: 'appointment_overlay',
    path: 'is_emergency',
  })
  let skip_appointment_reset = useDataValue({
    viewPath: props.viewPath,
    context: 'appointment_overlay',
    path: 'skip_appointment_reset',
  })
  let change = useDataChange({
    viewPath: props.viewPath,
    context: 'appointment_overlay',
    path: 'appointment_id',
  })

  return async function onChange(next) {
    if (!next?.length) return
    /**
     * If the appointment overlay was opened with external args and appointment id is defined,
     * setExistingAppointment was already triggered in AppointmentData. Here, we have access to the
     * calculated approximate or optimal date if applies, and in such case we need to
     * change the optimal date at tab level. If it's a new appointment, appointment_id is null
     * and instead we need to call setNewAppointment with approximateDate.
     */
    if (is_external_any) {
      if (appointment_id) {
        let appointment = next.find(v => v._id === appointment_id)
        if (appointment && appointment.approximateDate !== null) {
          submitTab({
            type: 'setOptimalDate',
            date: appointment.approximateDate,
          })
        }
        return
      } else {
        let newAppointment = next.find(v => v.id === 'new')
        submit({
          type: 'setNewAppointment',
          approximateDate: newAppointment.approximateDate || null,
        })
      }
    }

    /**
     * If appointment overlay was opened in emergency mode, we only need to set
     * new appointment to display slots
     */
    if (is_emergency && !appointment_id) {
      submit({ type: 'setNewAppointment' })
      return
    }

    /**
     * If appointment id is already set and exists in the timeline,
     * or it's 'new', we do nothing.
     */
    let already_selected_appointment = next.find(v => v._id === appointment_id)
    if (already_selected_appointment || appointment_id === 'new') return

    /**
     * Instead, if we are selecting the appointment from existing patient searched inside
     * appointment overlay, we should choose the preferred one from the timeline
     * If there is a preferred, we call setExistingAppointment method to set the data
     * in the appointment overlay context and update the optimal date if this applies
     */
    let preferred = next.filter(
      v => isUnscheduledAppointment(v) && v.id !== 'new'
    )?.[0]

    // Skip resetting the schedule if the patient is coming from new
    if (skip_appointment_reset) {
      change('new')
      return
    }

    if (preferred) {
      // There is at least one unscheduled appointment in the list of appointments
      // This unscheduled appointment, either has no booking associated or the
      // booking is in NO_SHOW, OFFICE_CANCELLED or PATIENT_CANCELLED state
      submit({
        type: 'setExistingAppointment',
        id: preferred._id,
        type_id: preferred.type._id,
        type_name: preferred.type.display_name,
        treatment_id: preferred.tx._id,
        duration: preferred.duration,
        approximateDate: preferred.approximateDate,
        location: {
          id: preferred.tx.organization._id,
          vaxiom_id: preferred.tx.organization.id,
          name: preferred.tx.organization.name,
          time_zone_id: preferred.tx.organization.time_zone_id,
        },
      })
    } else {
      let newAppointment = next.find(v => v.id === 'new')
      submit({
        type: 'setNewAppointment',
        approximateDate: newAppointment.approximateDate || null,
      })
    }
  }
}
